import { useEffect, useRef } from 'react';

/* ── AMKO brand colors ────────────────────────────────────
   Dark mode aura  → AMKO orange  #F5A623 = (0.961, 0.651, 0.137)
   Light mode aura → AMKO navy   #1A3A8F = (0.102, 0.227, 0.561)
   Transition: ease-out quadratic over 60 frames
   ───────────────────────────────────────────────────────── */

const DARK_AURA = [0.961, 0.651, 0.137];   // #F5A623 orange
const DARK_BG1  = [0.05,  0.10,  0.25 ];   // deep navy shadow
const DARK_BG2  = [0.02,  0.05,  0.18 ];   // darker navy

const LIGHT_AURA = [0.102, 0.227, 0.561];  // #1A3A8F navy
const LIGHT_BG1  = [0.08,  0.18,  0.48 ]; // soft blue glow
const LIGHT_BG2  = [0.12,  0.27,  0.60 ]; // brighter blue

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform vec2  u_res;
uniform vec2  u_mouse;
uniform float u_time;
uniform vec3  u_aura;   /* lerped smoke base color          */
uniform vec3  u_bg1;    /* gradient top (very subtle)       */
uniform vec3  u_bg2;    /* gradient bottom (very subtle)    */

mat2 rot(float a) {
  float c = cos(a), s = sin(a);
  return mat2(c, -s, s, c);
}
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i),                hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0,1.0)), hash(i + vec2(1.0,1.0)), u.x),
    u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 6; i++) {
    v += a * noise(p);
    p  = rot(0.6) * p * 2.1 + vec2(0.1 * float(i));
    a *= 0.45;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  uv.y = 1.0 - uv.y;

  float asp  = u_res.x / u_res.y;
  float dist = length((uv - u_mouse) * vec2(asp, 1.0));

  float t     = u_time * 0.18;
  vec2  q     = vec2(fbm(uv * 2.5 + t),
                     fbm(uv * 2.5 + vec2(1.3, 0.7) + t));
  vec2  r     = vec2(fbm(uv * 3.0 + q + vec2(1.7, 9.2) + t * 0.9),
                     fbm(uv * 3.0 + q + vec2(8.3, 2.8) + t * 0.9));
  float shape = fbm(uv * 2.0 + r);

  /* smoke falloff */
  float smoke = exp(-dist * 2.8) * (shape * 0.72 + 0.28);
  smoke = pow(smoke, 1.4) * 2.4;

  /* aura: shift hue slightly around the base brand color using fbm */
  float shift = fract(shape * 0.4 + t * 0.08);
  vec3  col   = mix(u_aura, u_aura * vec3(1.3, 0.9, 1.5), shift * 0.35);
  col        += mix(vec3(0.0), vec3(0.15, 0.25, 0.55), smoothstep(0.5, 1.0, shape));

  /* very subtle ambient gradient (additive — barely visible) */
  vec3 bg = mix(u_bg1, u_bg2, uv.y) * 0.06;

  /* AdditiveBlending output */
  gl_FragColor = vec4(col * smoke + bg, smoke * 0.85);
}
`;

function compile(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  return s;
}
function createProgram(gl) {
  const p = gl.createProgram();
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER,   VERT));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(p);
  return p;
}

/* ease-out quadratic: f(t) = 1 − (1−t)² */
function easeOutQuad(t) { return 1 - (1 - t) * (1 - t); }

export default function CursorSmoke() {
  const canvasRef = useRef(null);
  const dotRef    = useRef(null);

  useEffect(() => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const canvas = canvasRef.current;
    const dot    = dotRef.current;
    const gl = canvas.getContext('webgl', { premultipliedAlpha: false, alpha: true });
    if (!gl) return;

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // AdditiveBlending

    const prog = createProgram(gl);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER,
      new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes   = gl.getUniformLocation(prog, 'u_res');
    const uMouse = gl.getUniformLocation(prog, 'u_mouse');
    const uTime  = gl.getUniformLocation(prog, 'u_time');
    const uAura  = gl.getUniformLocation(prog, 'u_aura');
    const uBg1   = gl.getUniformLocation(prog, 'u_bg1');
    const uBg2   = gl.getUniformLocation(prog, 'u_bg2');

    /* theme detection */
    const isDark = () =>
      document.documentElement.getAttribute('data-theme') === 'dark';

    /* lerped uniform state — start matching current theme */
    let fromAura = [...(isDark() ? DARK_AURA  : LIGHT_AURA)];
    let fromBg1  = [...(isDark() ? DARK_BG1   : LIGHT_BG1)];
    let fromBg2  = [...(isDark() ? DARK_BG2   : LIGHT_BG2)];
    let curAura  = [...fromAura];
    let curBg1   = [...fromBg1];
    let curBg2   = [...fromBg2];
    let toAura   = [...curAura];
    let toBg1    = [...curBg1];
    let toBg2    = [...curBg2];

    /* 0 = transition in progress, 1 = done */
    let transProgress = 1.0;

    const startTransition = () => {
      fromAura = [...curAura];
      fromBg1  = [...curBg1];
      fromBg2  = [...curBg2];
      toAura   = isDark() ? [...DARK_AURA]  : [...LIGHT_AURA];
      toBg1    = isDark() ? [...DARK_BG1]   : [...LIGHT_BG1];
      toBg2    = isDark() ? [...DARK_BG2]   : [...LIGHT_BG2];
      transProgress = 0.0;
    };

    /* switch blend mode and dot style for each theme */
    const applyBlend = () => {
      const dark = isDark();
      canvas.style.mixBlendMode = dark ? 'screen' : 'multiply';
      if (dot) {
        dot.style.background  = dark ? '#ffffff' : '#1A3A8F';
        dot.style.mixBlendMode = dark ? 'screen' : 'normal';
        dot.style.boxShadow   = dark
          ? '0 0 8px #fff, 0 0 18px rgba(245,166,35,0.8)'
          : '0 0 8px rgba(26,58,143,0.5), 0 0 16px rgba(26,58,143,0.3)';
      }
    };
    applyBlend();

    const themeObs = new MutationObserver(() => { applyBlend(); startTransition(); });
    themeObs.observe(document.documentElement,
      { attributes: true, attributeFilter: ['data-theme'] });

    /* resize */
    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener('resize', resize, { passive: true });

    /* mouse — raw for dot, lerped (0.055) for smoke */
    let rawX = -1, rawY = -1;
    let lerpX = 0.5, lerpY = 0.5;

    const onMove = e => {
      rawX = e.clientX / window.innerWidth;
      rawY = e.clientY / window.innerHeight;
      if (dot) {
        dot.style.transform = `translate(${e.clientX - 5}px, ${e.clientY - 5}px)`;
        dot.style.opacity   = '1';
      }
    };
    const onLeave = () => { if (dot) dot.style.opacity = '0'; };
    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);

    document.body.style.cursor = 'none';

    const start = performance.now();
    let raf;

    const lerp3 = (from, to, t) =>
      from.map((v, i) => v + (to[i] - v) * t);

    const render = () => {
      const time = (performance.now() - start) * 0.001;

      /* ease-out quadratic transition over 60 frames (1/60 per frame) */
      if (transProgress < 1.0) {
        transProgress = Math.min(1.0, transProgress + 1 / 60);
        const eased = easeOutQuad(transProgress);
        curAura = lerp3(fromAura, toAura, eased);
        curBg1  = lerp3(fromBg1,  toBg1,  eased);
        curBg2  = lerp3(fromBg2,  toBg2,  eased);
      }

      /* lerp 0.055 — natural heavy drag */
      if (rawX >= 0) {
        lerpX += (rawX - lerpX) * 0.055;
        lerpY += (rawY - lerpY) * 0.055;
      }

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(uRes,   canvas.width, canvas.height);
      gl.uniform2f(uMouse, lerpX,        lerpY);
      gl.uniform1f(uTime,  time);
      gl.uniform3fv(uAura, curAura);
      gl.uniform3fv(uBg1,  curBg1);
      gl.uniform3fv(uBg2,  curBg2);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      raf = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      themeObs.disconnect();
      window.removeEventListener('resize',    resize);
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.body.style.cursor = '';
      gl.deleteProgram(prog);
      gl.deleteBuffer(buf);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed', inset: 0,
          width: '100%', height: '100%',
          pointerEvents: 'none',
          zIndex: 9997,
        }}
      />
      <div
        ref={dotRef}
        style={{
          position: 'fixed', top: 0, left: 0,
          width: 10, height: 10,
          borderRadius: '50%',
          background: '#ffffff',
          pointerEvents: 'none',
          zIndex: 9999,
          opacity: 0,
          mixBlendMode: 'screen',
          boxShadow: '0 0 8px #fff, 0 0 18px rgba(245,166,35,0.8)',
          transition: 'opacity 0.2s, background 0.4s, box-shadow 0.4s',
          willChange: 'transform',
        }}
      />
    </>
  );
}
